import { motion } from "framer-motion";
import { ArrowLeft, ArrowUpRight, Cpu, Activity, Coins, Newspaper } from "lucide-react";
import { Link } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Seo } from "@/components/Seo";
import { useLang } from "@/i18n";

const meta = [
  { icon: Activity, img: "/images/press-teo-salute.webp", imgAlt: "La pagina de Il Piccolo dedicata a TEO Salute" },
  { icon: Cpu, img: "/images/notarchain-stage.jpg", imgAlt: "Yuri Selvaggio presenta Notarchain sul palco" },
  { icon: Coins, img: "/images/press-bricsgold.jpg", imgAlt: "L'articolo de Il Piccolo su BRICS Gold" },
];

export default function Yurax() {
  const { t } = useLang();
  return (
    <main className="min-h-screen" data-testid="yurax-page">
      <Seo
        title="Progetti Speciali — TEO Salute, Notarchain.eu e BRICS Gold"
        description="L'incubatore di Yuri Selvaggio: telemedicina con TEO Salute, certificazione blockchain con Notarchain.eu e BRICS Gold per il distretto orafo di Valenza."
        path="/yurax"
      />
      <Navbar />
      <section className="relative pt-32 pb-16 px-5 sm:px-8 overflow-hidden">
        <img src="/images/logo-watermark.png" alt="" aria-hidden="true"
          className="pointer-events-none select-none absolute -right-24 top-10 w-[30rem] max-w-none" />
        <div className="relative max-w-5xl mx-auto">
          <Link to="/" data-testid="yurax-back-home"
            className="inline-flex items-center gap-2 text-sm font-medium text-[#3D5A66] hover:text-[#0284C7] transition-colors">
            <ArrowLeft size={15} /> {t.yurax.back}
          </Link>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          >
            <span className="font-mono-ed text-xs uppercase tracking-[0.22em] text-[#0284C7]">{t.yurax.label}</span>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-[#0B2530] mt-4 leading-[1.05]">
              {t.yurax.h1a}<em className="font-serif-ed italic font-medium text-[#06B6D4]">{t.yurax.h1em}</em>
            </h1>
            <p className="text-[#3D5A66] mt-6 max-w-2xl text-base sm:text-lg leading-relaxed">
              {t.yurax.intro}
            </p>
          </motion.div>
        </div>
      </section>

      <section className="px-5 sm:px-8 max-w-5xl mx-auto pb-24">
        {t.yurax.projects.map((p, i) => {
          const m = meta[i];
          return (
            <motion.article
              key={p.title}
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-70px" }}
              transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
              className={`grid grid-cols-1 md:grid-cols-2 gap-10 items-center py-14 ${i > 0 ? "border-t border-[#DCEEF5]" : ""}`}
              data-testid={`yurax-project-0${i + 1}`}
            >
              <div className={i % 2 === 1 ? "md:order-2" : ""}>
                <div className="flex items-center gap-3">
                  <span className="watermark text-5xl text-[#DFF0F8]">{`0${i + 1}`}</span>
                  <span className="inline-flex items-center gap-2 rounded-full bg-[#ECFEFF] border border-[#A5F3FC] px-4 py-1.5 text-xs font-mono-ed uppercase tracking-[0.14em] text-[#0369A1]">
                    <m.icon size={13} /> {p.tag}
                  </span>
                </div>
                <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-extrabold text-[#0B2530] mt-5">{p.title}</h2>
                <p className="font-serif-ed text-lg sm:text-xl text-[#3D5A66] leading-relaxed mt-4">{p.text}</p>
                <p className="text-sm text-[#5F7E8A] italic mt-4">{p.detail}</p>
                <div className="flex flex-wrap items-center gap-4 mt-6">
                  {p.link && (
                    <a href={p.link} target="_blank" rel="noopener noreferrer" data-testid={`yurax-link-0${i + 1}`}
                      className="group inline-flex items-center gap-1.5 rounded-full bg-[#0284C7] text-white text-sm font-semibold px-6 py-3 hover:bg-[#075E8A] transition-colors duration-300 active:scale-95">
                      {p.linkLabel}
                      <ArrowUpRight size={14} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                    </a>
                  )}
                  <span className="inline-flex items-center gap-2 text-xs font-mono-ed uppercase tracking-[0.14em] text-[#5F7E8A]">
                    <Newspaper size={13} className="text-[#E8894B]" /> {p.press}
                  </span>
                </div>
              </div>
              <div className={`rounded-3xl overflow-hidden img-frame border border-white ${i % 2 === 1 ? "md:order-1" : ""}`}>
                <img src={m.img} alt={m.imgAlt} loading="lazy"
                  className="w-full h-72 sm:h-80 object-cover object-top hover:scale-[1.03] transition-transform duration-700" />
              </div>
            </motion.article>
          );
        })}
      </section>

      <section className="px-5 sm:px-8 max-w-5xl mx-auto pb-28">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          className="rounded-3xl bg-gradient-to-br from-[#ECFEFF] to-[#F0F9FF] border border-[#BAE6FD] p-10 sm:p-14 text-center"
        >
          <h3 className="font-display text-2xl sm:text-3xl font-extrabold text-[#0B2530]">
            {t.yurax.ctaTitle}
          </h3>
          <p className="text-[#3D5A66] mt-4 max-w-xl mx-auto">
            {t.yurax.ctaText}
          </p>
          <a href="/#contatti" data-testid="yurax-cta-contatti"
            className="inline-flex items-center gap-2 mt-8 rounded-full bg-[#0B2530] text-white font-semibold px-8 py-3.5 hover:bg-[#0284C7] transition-colors duration-300 active:scale-95">
            {t.yurax.ctaBtn} <ArrowUpRight size={16} />
          </a>
        </motion.div>
      </section>
      <Footer />
    </main>
  );
}
